import type { ApiTypes } from '@principle-theorem/principle-api';
import { Timestamp } from '@principle-theorem/shared';
import { ApiScope } from '../api-key';

export enum ApiWebhookEvent {
  AccountCreditCreated = 'account-credit.created',
  AccountCreditUpdated = 'account-credit.updated',
  AppointmentCreated = 'appointment.created',
  AppointmentUpdated = 'appointment.updated',
  EmailCreated = 'email.created',
  InvoiceCreated = 'invoice.created',
  InvoiceUpdated = 'invoice.updated',
  FileCreated = 'file.created',
  PatientCreated = 'patient.created',
  PatientUpdated = 'patient.updated',
  SMSMessageCreated = 'message.created',
  TransactionCreated = 'transaction.created',
  TransactionUpdated = 'transaction.updated',
  TreatmentPlanCreated = 'treatment-plan.created',
  TreatmentPlanUpdated = 'treatment-plan.updated',
  TreatmentStepCreated = 'treatment-step.created',
  TreatmentStepUpdated = 'treatment-step.updated',
}

export type ApiWebhookEventData =
  | {
      eventType:
        | ApiWebhookEvent.AppointmentCreated
        | ApiWebhookEvent.AppointmentUpdated;
      data: ApiTypes.Appointment;
    }
  | {
      eventType:
        | ApiWebhookEvent.AccountCreditCreated
        | ApiWebhookEvent.AccountCreditUpdated;
      data: ApiTypes.AccountCredit;
    }
  | {
      eventType: ApiWebhookEvent.EmailCreated;
      data: ApiTypes.Email;
    }
  | {
      eventType:
        | ApiWebhookEvent.InvoiceCreated
        | ApiWebhookEvent.InvoiceUpdated;
      data: ApiTypes.Invoice;
    }
  | {
      eventType: ApiWebhookEvent.FileCreated;
      data: ApiTypes.File;
    }
  | {
      eventType: ApiWebhookEvent.SMSMessageCreated;
      data: ApiTypes.SMSMessage;
    }
  | {
      eventType:
        | ApiWebhookEvent.PatientCreated
        | ApiWebhookEvent.PatientUpdated;
      data: ApiTypes.Patient;
    }
  | {
      eventType:
        | ApiWebhookEvent.TransactionCreated
        | ApiWebhookEvent.TransactionUpdated;
      data: ApiTypes.Transaction;
    }
  | {
      eventType:
        | ApiWebhookEvent.TreatmentPlanCreated
        | ApiWebhookEvent.TreatmentPlanUpdated;
      data: ApiTypes.TreatmentPlan;
    }
  | {
      eventType:
        | ApiWebhookEvent.TreatmentStepCreated
        | ApiWebhookEvent.TreatmentStepUpdated;
      data: ApiTypes.TreatmentStep;
    };

export enum WebhookEventStatus {
  Pending = 'pending',
  InProgress = 'inProgress',
  Success = 'success',
  Failed = 'failed',
}

export interface IWebhookEvent {
  eventType: ApiWebhookEvent;
  data: object;
  timestamp: Timestamp;
  status: WebhookEventStatus;
  retryAttempts: number;
  cloudTaskId?: string;
  errorMessage?: string;
}

export const API_SCOPE_TO_WEBHOOK_COMPATIBILITY: Record<
  ApiScope,
  ApiWebhookEvent[]
> = {
  [ApiScope.PatientsRead]: [
    ApiWebhookEvent.PatientCreated,
    ApiWebhookEvent.PatientUpdated,
  ],
  [ApiScope.PatientsWrite]: [
    ApiWebhookEvent.PatientCreated,
    ApiWebhookEvent.PatientUpdated,
  ],
  [ApiScope.AppointmentsRead]: [
    ApiWebhookEvent.AppointmentCreated,
    ApiWebhookEvent.AppointmentUpdated,
  ],
  [ApiScope.AppointmentsWrite]: [
    ApiWebhookEvent.AppointmentCreated,
    ApiWebhookEvent.AppointmentUpdated,
  ],
  [ApiScope.AccountsRead]: [
    ApiWebhookEvent.InvoiceCreated,
    ApiWebhookEvent.InvoiceUpdated,
    ApiWebhookEvent.TransactionCreated,
    ApiWebhookEvent.TransactionUpdated,
    ApiWebhookEvent.AccountCreditCreated,
  ],
  [ApiScope.AccountsWrite]: [
    ApiWebhookEvent.InvoiceCreated,
    ApiWebhookEvent.InvoiceUpdated,
    ApiWebhookEvent.TransactionCreated,
    ApiWebhookEvent.TransactionUpdated,
    ApiWebhookEvent.AccountCreditCreated,
  ],
  [ApiScope.FileRead]: [ApiWebhookEvent.FileCreated],
  [ApiScope.FileWrite]: [ApiWebhookEvent.FileCreated],
  [ApiScope.MessagesRead]: [ApiWebhookEvent.SMSMessageCreated],
  [ApiScope.EmailsRead]: [ApiWebhookEvent.EmailCreated],
  [ApiScope.EmailsWrite]: [ApiWebhookEvent.EmailCreated],
  [ApiScope.MessagesWrite]: [ApiWebhookEvent.SMSMessageCreated],
  [ApiScope.TagsRead]: [],
  [ApiScope.TagsWrite]: [],
  [ApiScope.PracticesRead]: [],
  [ApiScope.AvailabilityRead]: [],
  [ApiScope.NotificationsWrite]: [],
  [ApiScope.InteractionsWrite]: [],
  [ApiScope.TreatmentPlansRead]: [
    ApiWebhookEvent.TreatmentPlanCreated,
    ApiWebhookEvent.TreatmentPlanUpdated,
    ApiWebhookEvent.TreatmentStepCreated,
    ApiWebhookEvent.TreatmentStepUpdated,
  ],
  [ApiScope.TreatmentPlansWrite]: [
    ApiWebhookEvent.TreatmentPlanCreated,
    ApiWebhookEvent.TreatmentPlanUpdated,
    ApiWebhookEvent.TreatmentStepCreated,
    ApiWebhookEvent.TreatmentStepUpdated,
  ],
};

export const WEBHOOK_EVENT_TO_API_SCOPE_COMPATABILITY: Record<
  ApiWebhookEvent,
  ApiScope[]
> = {
  [ApiWebhookEvent.AppointmentCreated]: [
    ApiScope.AppointmentsRead,
    ApiScope.AppointmentsWrite,
  ],
  [ApiWebhookEvent.AppointmentUpdated]: [
    ApiScope.AppointmentsRead,
    ApiScope.AppointmentsWrite,
  ],
  [ApiWebhookEvent.AccountCreditCreated]: [
    ApiScope.AccountsRead,
    ApiScope.AccountsWrite,
  ],
  [ApiWebhookEvent.AccountCreditUpdated]: [
    ApiScope.AccountsRead,
    ApiScope.AccountsWrite,
  ],
  [ApiWebhookEvent.EmailCreated]: [ApiScope.EmailsRead, ApiScope.EmailsWrite],
  [ApiWebhookEvent.InvoiceCreated]: [
    ApiScope.AccountsRead,
    ApiScope.AccountsWrite,
  ],
  [ApiWebhookEvent.InvoiceUpdated]: [
    ApiScope.AccountsRead,
    ApiScope.AccountsWrite,
  ],
  [ApiWebhookEvent.FileCreated]: [ApiScope.FileRead, ApiScope.FileWrite],
  [ApiWebhookEvent.SMSMessageCreated]: [
    ApiScope.MessagesRead,
    ApiScope.MessagesWrite,
  ],
  [ApiWebhookEvent.PatientCreated]: [
    ApiScope.PatientsRead,
    ApiScope.PatientsWrite,
  ],
  [ApiWebhookEvent.PatientUpdated]: [
    ApiScope.PatientsRead,
    ApiScope.PatientsWrite,
  ],
  [ApiWebhookEvent.TransactionCreated]: [
    ApiScope.AccountsRead,
    ApiScope.AccountsWrite,
  ],
  [ApiWebhookEvent.TransactionUpdated]: [
    ApiScope.AccountsRead,
    ApiScope.AccountsWrite,
  ],
  [ApiWebhookEvent.TreatmentPlanCreated]: [
    ApiScope.TreatmentPlansRead,
    ApiScope.TreatmentPlansWrite,
  ],
  [ApiWebhookEvent.TreatmentPlanUpdated]: [
    ApiScope.TreatmentPlansRead,
    ApiScope.TreatmentPlansWrite,
  ],
  [ApiWebhookEvent.TreatmentStepCreated]: [
    ApiScope.TreatmentPlansRead,
    ApiScope.TreatmentPlansWrite,
  ],
  [ApiWebhookEvent.TreatmentStepUpdated]: [
    ApiScope.TreatmentPlansRead,
    ApiScope.TreatmentPlansWrite,
  ],
};
